import { FC, useState, useEffect } from 'react'
import { useNoSaleReasons } from '../hooks/useNoSaleReasons'

interface NoSaleReasonsTabProps {
  pluginspaceId: number
}

export const NoSaleReasonsTab: FC<NoSaleReasonsTabProps> = ({ pluginspaceId }) => {
  const { reasons, loading, loadReasonStatistics } = useNoSaleReasons(pluginspaceId)
  const [days, setDays] = useState(7)

  useEffect(() => {
    loadReasonStatistics(days)
  }, [])

  const handleFilterDays = async (newDays: number) => {
    setDays(newDays)
    await loadReasonStatistics(newDays)
  }

  const reasonLabels: Record<string, string> = {
    size_unavailable: 'Tamanho Indisponível',
    color_unavailable: 'Cor Indisponível',
    price_too_high: 'Preço Muito Alto',
    did_not_find_product: 'Não Encontrou Produto',
    just_browsing: 'Apenas Navegando',
    customer_left: 'Cliente Saiu',
    other: 'Outro',
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Período</h3>
        </div>
        <div className='card-body'>
          <div className='btn-group' role='group'>
            <button
              type='button'
              className={`btn btn-outline-primary ${days === 7 ? 'active' : ''}`}
              onClick={() => handleFilterDays(7)}
            >
              Últimos 7 dias
            </button>
            <button
              type='button'
              className={`btn btn-outline-primary ${days === 30 ? 'active' : ''}`}
              onClick={() => handleFilterDays(30)}
            >
              Últimos 30 dias
            </button>
            <button
              type='button'
              className={`btn btn-outline-primary ${days === 90 ? 'active' : ''}`}
              onClick={() => handleFilterDays(90)}
            >
              Últimos 90 dias
            </button>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Motivos de Não Venda</h3>
        </div>
        <div className='card-body'>
          {reasons.length === 0 ? (
            <div className='text-center py-10'>
              <p className='text-muted'>Nenhum motivo registrado neste período</p>
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 gy-7'>
                <thead>
                  <tr className='fw-bold fs-6 text-gray-800'>
                    <th style={{ width: '5%' }}>Rank</th>
                    <th>Motivo</th>
                    <th style={{ width: '15%' }} className='text-end'>
                      Quantidade
                    </th>
                    <th style={{ width: '25%' }} className='text-end'>
                      Percentual
                    </th>
                    <th style={{ width: '15%' }}>Barra</th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.map((reason, index) => (
                    <tr key={reason.code}>
                      <td className='fw-bold text-primary'>{index + 1}</td>
                      <td>
                        <span className='fw-bold'>{reasonLabels[reason.code] || reason.code}</span>
                      </td>
                      <td className='text-end'>{reason.count}</td>
                      <td className='text-end fw-bold'>{reason.percentage.toFixed(1)}%</td>
                      <td>
                        <div className='progress' style={{ height: '5px' }}>
                          <div
                            className='progress-bar'
                            role='progressbar'
                            style={{ width: `${reason.percentage}%` }}
                            aria-valuenow={reason.percentage}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Gráfico de Pizza simples (HTML + CSS) */}
      {reasons.length > 0 && (
        <div className='card mt-5'>
          <div className='card-header'>
            <h3 className='card-title'>Distribuição</h3>
          </div>
          <div className='card-body d-flex justify-content-center'>
            <div className='position-relative' style={{ width: '300px', height: '300px' }}>
              <div
                className='rounded-circle d-flex align-items-center justify-content-center fw-bold fs-3 text-white'
                style={{
                  width: '100%',
                  height: '100%',
                  background: `conic-gradient(
                    ${reasons
                      .map((r) => {
                        const hue = (reasons.indexOf(r) * 360) / reasons.length
                        return `hsl(${hue}, 70%, 60%) 0deg ${r.percentage * 3.6}deg`
                      })
                      .join(', ')}
                  )`,
                }}
              >
                <div className='text-center'>
                  <div className='fs-5 fw-bold'>Total</div>
                  <div className='fs-6'>{reasons.reduce((sum, r) => sum + r.count, 0)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legenda */}
      {reasons.length > 0 && (
        <div className='card mt-5'>
          <div className='card-header'>
            <h3 className='card-title'>Legenda</h3>
          </div>
          <div className='card-body'>
            <div className='row g-3'>
              {reasons.map((reason, index) => {
                const hue = (index * 360) / reasons.length
                return (
                  <div key={reason.code} className='col-md-6 col-lg-4'>
                    <div className='d-flex align-items-center'>
                      <div
                        className='rounded-circle me-2'
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: `hsl(${hue}, 70%, 60%)`,
                        }}
                      ></div>
                      <div className='flex-grow-1'>
                        <div className='fw-bold'>{reasonLabels[reason.code] || reason.code}</div>
                        <small className='text-muted'>{reason.count} ocorrências</small>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
